import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../../../db/firebase";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { transposeChords } from "../../../utils/transposeChords";
import { LoadingPage } from "../../../components/Loading";
import { SongArtwork } from "../../../components/SongArtwork";
import { YoutubeVideosFormEditor } from "../../../components/YoutubeVideosFormEditor";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineArrowLeft,
  HiOutlineMusicalNote,
  HiOutlinePhoto,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import {
  buildYoutubeFirestoreUpdate,
  getYoutubeLinksFromSong,
  prepareYoutubeLinksForSave,
} from "../../../utils/youtubeLinks";

export interface ChordVersion {
  id: string;
  label: string;
  content: string;
}

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-colors";

const labelClass =
  "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const EditSong: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [song, setSong] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [album, setAlbum] = useState("");
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>([""]);
  const [youtubeError, setYoutubeError] = useState("");
  const [artwork, setArtwork] = useState<File | null>(null);
  const [artworkPreview, setArtworkPreview] = useState<string | null>(null);
  const [artworkURL, setArtworkURL] = useState("");
  const [chordVersions, setChordVersions] = useState<ChordVersion[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      if (artworkPreview) URL.revokeObjectURL(artworkPreview);
    };
  }, [artworkPreview]);

  const handleTransposeVersion = (versionId: string, delta: number) => {
    setChordVersions((prev) =>
      prev.map((v) =>
        v.id === versionId
          ? { ...v, content: transposeChords(v.content, delta) }
          : v,
      ),
    );
  };

  const addChordVersion = () => {
    setChordVersions((prev) => [
      ...prev,
      { id: uuidv4(), label: "", content: "" },
    ]);
  };

  const removeChordVersion = (versionId: string) => {
    setChordVersions((prev) => prev.filter((v) => v.id !== versionId));
  };

  const updateChordVersion = (
    versionId: string,
    updates: Partial<Pick<ChordVersion, "label" | "content">>,
  ) => {
    setChordVersions((prev) =>
      prev.map((v) => (v.id === versionId ? { ...v, ...updates } : v)),
    );
  };

  useEffect(() => {
    const fetchSong = async () => {
      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      try {
        const songDoc = await getDoc(doc(db, "songs", id));
        if (!songDoc.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const songData = songDoc.data();
        setSong(songData);
        setTitle(songData.title || "");
        setArtist(songData.artist || "");
        setAlbum(songData.album || "");
        const loadedYoutubeLinks = getYoutubeLinksFromSong(songData);
        setYoutubeLinks(
          loadedYoutubeLinks.length > 0 ? loadedYoutubeLinks : [""],
        );
        setArtworkURL(songData.artworkURL || "");
        if (
          songData.chordVersions &&
          Array.isArray(songData.chordVersions) &&
          songData.chordVersions.length > 0
        ) {
          setChordVersions(
            songData.chordVersions.map((v: any) => ({
              id: v.id || uuidv4(),
              label: v.label || "",
              content: v.content || "",
            })),
          );
        } else {
          const legacyChords = songData.chords || "";
          setChordVersions(
            legacyChords
              ? [{ id: uuidv4(), label: "Chords", content: legacyChords }]
              : [],
          );
        }
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSong();
  }, [id]);

  const handleArtworkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (artworkPreview) URL.revokeObjectURL(artworkPreview);
    setArtwork(file);
    setArtworkPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError("");
    setSuccessMessage("");

    if (title.trim() === "" || artist.trim() === "") {
      setSaveError(t("import_error") || "Title and artist are required.");
      return;
    }

    if (!id || !song) return;

    setUploading(true);
    let artworkURLToUpdate = artworkURL;

    try {
      if (artwork) {
        const storageRef = ref(storage, `artworks/${Date.now()}_${artwork.name}`);
        const uploadTask = uploadBytesResumable(storageRef, artwork);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            undefined,
            (error) => {
              console.error("Error uploading artwork: ", error);
              reject(error);
            },
            async () => {
              artworkURLToUpdate = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            },
          );
        });
      }

      const chordVersionsToSave = chordVersions.filter(
        (v) => v.label.trim() !== "" || v.content.trim() !== "",
      );
      const chordsForCompat =
        chordVersionsToSave.length > 0 ? chordVersionsToSave[0].content : "";
      const { links: savedYoutubeLinks, error: youtubeValidationError } =
        prepareYoutubeLinksForSave(youtubeLinks);
      if (youtubeValidationError === "invalid") {
        setYoutubeError(
          t("invalidYouTubeUrl") || "Please enter a valid YouTube URL.",
        );
        setUploading(false);
        return;
      }
      if (youtubeValidationError === "max") {
        setYoutubeError(
          t("maxYouTubeVideosReached", { max: 10 }) ||
            "You can add up to 10 videos.",
        );
        setUploading(false);
        return;
      }
      setYoutubeError("");

      await updateDoc(doc(db, "songs", id), {
        title: title.trim(),
        artist: artist.trim(),
        album: album.trim(),
        ...buildYoutubeFirestoreUpdate(savedYoutubeLinks),
        artworkURL: artworkURLToUpdate,
        chords: chordsForCompat,
        chordVersions: chordVersionsToSave,
      });

      setSuccessMessage(t("songUpdated") || "Song updated");
      setTimeout(() => navigate(`/songs/${id}`), 700);
    } catch (err: any) {
      console.error(err);
      setSaveError(
        err?.message || t("errorSavingSong") || "Failed to save song.",
      );
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <LoadingPage message={t("loading")} />;
  }

  if (notFound) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center">
        <HiOutlineMusicalNote className="w-14 h-14 mx-auto text-gray-400 mb-3 opacity-50" />
        <p className="text-gray-700 dark:text-gray-200 font-medium mb-4">
          {t("songNotFound") || "Song not found"}
        </p>
        <Link to="/songs" className={glassBtn}>
          <HiOutlineArrowLeft className="w-4 h-4" />
          {t("backToSongs") || "Back to songs"}
        </Link>
      </div>
    );
  }

  const displayArt = artworkPreview || artworkURL;

  return (
    <div className="max-w-3xl mx-auto space-y-5 pb-28">
      <Link
        to={`/songs/${id}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-[#5932EA] transition-colors"
      >
        <HiOutlineArrowLeft className="w-4 h-4" />
        {t("back") || "Back"}
      </Link>

      <div className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border border-gray-200/70 dark:border-gray-600 shrink-0 bg-gray-100 dark:bg-gray-700">
            {displayArt ? (
              <img
                src={displayArt}
                alt={title || t("artwork")}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#5932EA]">
                <HiOutlineMusicalNote className="w-8 h-8" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-[#5932EA] dark:text-[#c4b5fd] mb-1">
              {t("edit_song")}
            </p>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight truncate">
              {title || t("untitledSong") || "Untitled song"}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {[artist, album].filter(Boolean).join(" · ") ||
                t("editSongSubtitle") ||
                "Update details, chords, and media"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleUpdate} className="space-y-5">
        {/* Details */}
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <HiOutlineMusicalNote className="w-5 h-5 text-[#5932EA]" />
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              {t("details") || "Details"}
            </h2>
          </div>

          <div>
            <label htmlFor="title" className={labelClass}>
              {t("title")}
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className={inputBase}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="artist" className={labelClass}>
                {t("artist")}
              </label>
              <input
                type="text"
                id="artist"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                required
                className={inputBase}
                maxLength={100}
              />
            </div>
            <div>
              <label htmlFor="album" className={labelClass}>
                {t("album")}
              </label>
              <input
                type="text"
                id="album"
                value={album}
                onChange={(e) => setAlbum(e.target.value)}
                className={inputBase}
                maxLength={100}
              />
            </div>
          </div>

        </section>

        {/* Artwork */}
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-2">
            <HiOutlinePhoto className="w-5 h-5 text-[#5932EA]" />
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              {t("artwork")}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <label className="flex-1 w-full cursor-pointer min-w-0">
              <div className="flex items-center gap-3 px-5 py-4 rounded-xl border border-dashed border-[#5932EA]/30 bg-[#5932EA]/5 dark:bg-[#5932EA]/10 hover:bg-[#5932EA]/10 transition-colors">
                <HiOutlinePhoto className="w-6 h-6 text-[#5932EA] shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
                  {artwork
                    ? artwork.name
                    : t("chooseFileButton") || "Choose file"}
                </span>
              </div>
              <input
                type="file"
                id="artwork"
                accept="image/*"
                onChange={handleArtworkChange}
                className="hidden"
              />
            </label>
            {displayArt && (
              <div className="w-28 h-28 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 shrink-0">
                {artworkPreview ? (
                  <img
                    src={artworkPreview}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <SongArtwork
                    src={artworkURL}
                    alt={t("artwork")}
                    className="w-full h-full"
                    iconClassName="w-10 h-10"
                  />
                )}
              </div>
            )}
          </div>
        </section>

        {/* YouTube */}
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
            {t("youtubeVideos") || "YouTube Videos"}
          </h2>
          <YoutubeVideosFormEditor
            links={youtubeLinks}
            onChange={(links) => {
              setYoutubeLinks(links);
              setYoutubeError("");
            }}
            error={youtubeError}
            fieldIdPrefix="edit-song-youtube"
          />
        </section>

        {/* Chord versions */}
        <section className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/70 dark:bg-gray-800/50 backdrop-blur-sm p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-5 h-5 text-[#5932EA]" />
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("chords") || "Chords"}
              </h2>
            </div>
            <button type="button" onClick={addChordVersion} className={glassBtn}>
              <HiOutlinePlus className="w-4 h-4" />
              {t("addChordVersion") || "Add chord version"}
            </button>
          </div>

          {chordVersions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-600 py-8 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
              {t("noChordVersionsYet") ||
                "No chord versions yet. Add guitar, piano, bass, or other arrangements."}
            </div>
          ) : (
            <div className="space-y-3">
              {chordVersions.map((version, index) => (
                <div
                  key={version.id}
                  className="p-4 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/50 dark:bg-gray-900/30 backdrop-blur-sm"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-gray-400 w-6">
                      #{index + 1}
                    </span>
                    <input
                      type="text"
                      value={version.label}
                      onChange={(e) =>
                        updateChordVersion(version.id, {
                          label: e.target.value,
                        })
                      }
                      placeholder={
                        t("chordVersionLabel") || "e.g. Guitar, Piano, Bass"
                      }
                      className="flex-1 min-w-[10rem] px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                      maxLength={50}
                    />
                    {version.content && (
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-gray-500">
                          {t("transpose")}:
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleTransposeVersion(version.id, -1)
                          }
                          className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm"
                        >
                          −
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTransposeVersion(version.id, 1)}
                          className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm"
                        >
                          +
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeChordVersion(version.id)}
                      className="p-2 rounded-xl border border-red-300/40 bg-red-50/70 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100/80 transition-colors"
                      title={t("removeVersion") || "Remove version"}
                    >
                      <HiOutlineTrash className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={version.content}
                    onChange={(e) =>
                      updateChordVersion(version.id, {
                        content: e.target.value,
                      })
                    }
                    placeholder="e.g. Verse: Am - G - C - F | Chorus: C - G - Am - F"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 resize-y min-h-[100px]"
                    rows={5}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {(saveError || successMessage) && (
          <div
            className={`p-3 rounded-xl text-sm border ${
              saveError
                ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800"
                : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
            }`}
          >
            {saveError || successMessage}
          </div>
        )}

        {/* Sticky actions */}
        <div className="fixed bottom-0 inset-x-0 z-20 border-t border-gray-200/80 dark:border-gray-700/80 bg-white/85 dark:bg-gray-900/85 backdrop-blur-md pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-3xl mx-auto px-4 py-3 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Link
              to={`/songs/${id}`}
              className="h-11 px-5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 inline-flex items-center justify-center transition-colors"
            >
              {t("cancel") || "Cancel"}
            </Link>
            <button
              type="submit"
              disabled={uploading}
              className={`${glassBtn} h-11 px-6 sm:min-w-[10rem]`}
            >
              {uploading ? (
                <>
                  <span className="w-4 h-4 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin" />
                  {t("updating")}
                </>
              ) : (
                t("update_song")
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditSong;
